import React, { PureComponent, createRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import FontFaceObserver from 'fontfaceobserver';

import { getViewportWidth } from '../utils/window';
import '../scss/_tooltip.scss';

const Positions = {
  LEFT: 'tooltip_left',
  TOP: 'tooltip_top',
  RIGHT: 'tooltip_right',
  BOTTOM: 'tooltip_bottom',
  'BOTTOM-LEFT': 'tooltip_bottom-left',
  'BOTTOM-RIGHT': 'tooltip_bottom-right',
  'TOP-LEFT': 'tooltip_top-left',
  'TOP-RIGHT': 'tooltip_top-right',
};

const TOOLTIP_LABEL_CLASS = 'tooltip__label';
const WINDOW_MARGIN = 8;

export class Tooltip extends PureComponent {
  constructor(props) {
    super(props);
    this.labelInnerWidth = props.labelMaxWidth ? props.labelMaxWidth - 20 : 480; // 2x 10px horizontal paddings
    this.portalRef = createRef(null);
    this.state = {
      isVisible: false,
      isShowing: false, // visible with animation duration
      position: Positions[props.position.toUpperCase()],
      labelInnerMaxWidth: this.labelInnerWidth,
    };

    this.timer = null;
    this.mouseoutTimer = null;
  }

  tooltipLabel = createRef();
  tooltipWrapperRef = createRef();

  componentDidMount() {
    this.onFontLoaded();
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.state.isLabelMaxWidthSet) {
      this.setLabelMaxWidth();
    }
    if (this.props.disable && !prevProps.disable) {
      this.handleMouseLeave();
    }
    if (this.state.xycoords !== prevState.xycoords && this.props.adjustPortalPosition) {
      let newXYCoordsOverride = {};
      const tooltipLabel = this.portalRef.current?.querySelector(`.${TOOLTIP_LABEL_CLASS}`);
      const boundingRect = tooltipLabel?.getBoundingClientRect();
      if (tooltipLabel && boundingRect) {
        const viewportWidth = getViewportWidth();
        if (boundingRect.right + WINDOW_MARGIN > viewportWidth) {
          newXYCoordsOverride = {
            ...newXYCoordsOverride,
            left: viewportWidth - WINDOW_MARGIN - boundingRect.width / 2,
          };
        } else if (boundingRect.left <= WINDOW_MARGIN) {
          newXYCoordsOverride = {
            ...newXYCoordsOverride,
            left: WINDOW_MARGIN + boundingRect.width / 2,
          };
        }

        const tooltipWrapperRect = this.tooltipWrapperRef?.current?.getBoundingClientRect?.();

        if (boundingRect.top < 0 && tooltipWrapperRect) {
          newXYCoordsOverride = {
            ...newXYCoordsOverride,
            top: tooltipWrapperRect.bottom + tooltipWrapperRect.height + boundingRect.height,
          };
          delete newXYCoordsOverride.bottom;
        }
      }
      this.setState({ xycoordsOverride: newXYCoordsOverride });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
    clearTimeout(this.mouseoutTimer);
  }

  onFontLoaded = () => {
    const font = new FontFaceObserver('Avenir Next Medium');

    font.load().then(() => {
      this.setStyleToInitial();
    });
  };

  setStyleToInitial = () => {
    this.setState({
      labelInnerMaxWidth: this.labelInnerWidth,
      isLabelMaxWidthSet: false,
    });
  };

  setLabelMaxWidth = () => {
    const { onSetLabelMaxWidth } = this.props;
    if (this.tooltipLabelInner) {
      let contentWidth = Math.ceil(this.tooltipLabelInner.getBoundingClientRect().width);
      contentWidth = contentWidth <= this.labelInnerWidth ? contentWidth : this.labelInnerWidth;
      const widths = {
        labelInnerMaxWidth: contentWidth,
        isLabelMaxWidthSet: true,
      };
      this.setState(widths, () => {
        if (onSetLabelMaxWidth) {
          onSetLabelMaxWidth(widths);
        }
      });
    }
  };

  getPosition() {
    if (this.props.auto) {
      clearTimeout(this.process);
      clearTimeout(this.mouseoutTimer);
      const { isShowing, position: current } = this.state;
      if (isShowing) {
        return current;
      }

      let position = Positions.TOP;

      const coord = this.props.portal
        ? this.tooltipWrapperRef.current.getBoundingClientRect()
        : this.tooltipLabel.current.getBoundingClientRect();
      const { x, y, right } = coord;

      if (y < 0) {
        position = Positions.BOTTOM;
      }

      if (x < 0) {
        position = Positions.RIGHT;
      }

      if (right > window.innerWidth) {
        position = Positions.LEFT;
      }

      return position;
    } else {
      return this.state.position;
    }
  }

  getXYCoords() {
    const { useTooltipWrapperDimensions } = this.props;
    const coord = this.tooltipWrapperRef?.current?.getBoundingClientRect?.() ?? { x: 0, y: 0 };

    const { x, y, width } = coord;
    return { top: y, left: x, ...(useTooltipWrapperDimensions ? { width } : {}) };
  }

  handleMouseEnter = () => {
    if (this.props.disable) {
      return;
    }
    this.setStyleToInitial();
    const position = this.getPosition();

    const xycoords = this.getXYCoords();
    this.timer = setTimeout(() => {
      this.setState({
        isVisible: true,
        isShowing: true,
        position,
        xycoords,
        xycoordsOverride: {},
      });
    }, this.props.delayTime);
  };

  handleMouseLeave = () => {
    clearTimeout(this.timer);
    this.mouseoutTimer = setTimeout(() => {
      this.setState(
        {
          isVisible: false,
        },
        () => {
          this.process = setTimeout(() => {
            this.setState({
              position: this.props.auto
                ? Positions.TOP
                : Positions[this.props.position.toUpperCase()],
              isShowing: false,
            });
          }, 200); // Animation duration
        },
      );
    }, this.props.mouseoutDelay);
  };

  render() {
    const {
      className = '',
      style,
      handleClick,
      portal,
      getContentOnly,
      contentOnlyProps,
    } = this.props;
    const { isVisible, position, labelInnerMaxWidth, xycoords, xycoordsOverride } = this.state;
    const visible = isVisible ? 'tooltip_visible' : 'tooltip_hidden';
    const tooltipClassName = `tooltip ${position} ${visible} ${className}`;
    const contentOnlyClassName = `tooltip ${position} ${className} ${
      typeof contentOnlyProps?.isVisible === 'boolean' && !contentOnlyProps?.isVisible
        ? 'tooltip_hidden'
        : 'tooltip_visible'
    }`;

    let content = (
      <span ref={this.tooltipLabel} className={TOOLTIP_LABEL_CLASS}>
        <span
          className="tooltip__label-outer"
          style={{ width: `${this.labelInnerWidth}px`, maxWidth: `${labelInnerMaxWidth}px` }}
        >
          <span
            ref={(el) => {
              this.tooltipLabelInner = el;
            }}
            className="tooltip__label-inner"
          >
            {this.props.label}
          </span>
        </span>
      </span>
    );

    if (getContentOnly) {
      return <div className={contentOnlyClassName}>{content}</div>;
    }

    if (portal) {
      content = ReactDOM.createPortal(
        <div
          ref={this.portalRef}
          className={tooltipClassName}
          style={{
            ...style,
            position: 'fixed',
            zIndex: 9999,
            ...xycoords,
            ...(xycoordsOverride || {}),
          }}
        >
          {content}
        </div>,
        document.body,
      );
    }

    return (
      <div
        className={tooltipClassName}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        style={style}
        onClick={handleClick}
        ref={this.tooltipWrapperRef}
      >
        {content}
        {this.props.children}
      </div>
    );
  }
}

Tooltip.defaultProps = {
  delayTime: 500,
  position: 'top',
  auto: true,
  portal: false,
  getContentOnly: false,
  disable: false,
  useTooltipWrapperDimensions: false,
  adjustPortalPosition: false,
  mouseoutDelay: 0,
};

Tooltip.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  delayTime: PropTypes.number,
  position: PropTypes.oneOf([
    'top',
    'top-right',
    'top-left',
    'right',
    'bottom',
    'bottom-left',
    'bottom-right',
    'left',
  ]),
  auto: PropTypes.bool,
  labelMaxWidth: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  portal: PropTypes.bool,
  getContentOnly: PropTypes.bool,
  contentOnlyProps: PropTypes.shape({
    isVisible: PropTypes.bool,
  }),
  disable: PropTypes.bool,
  useTooltipWrapperDimensions: PropTypes.bool,
  adjustPortalPosition: PropTypes.bool,
  mouseoutDelay: PropTypes.number,
};
